import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SeenVideoRecord, SeenPersistenceSettings } from '../models/e621.models';

@Injectable({
  providedIn: 'root',
})
export class SeenPersistenceService {
  private readonly SEEN_VIDEOS_KEY = 'drawfiend-seen-videos';
  private readonly SEEN_SETTINGS_KEY = 'drawfiend-seen-settings';
  
  private seenVideos = new Map<number, number>(); // videoId -> timestamp
  private seenVideosCount$ = new BehaviorSubject<number>(0);

  constructor() {
    this.loadSeenVideos();
  }

  /**
   * Load seen media from localStorage
   */
  private loadSeenVideos(): void {
    try {
      const saved = localStorage.getItem(this.SEEN_VIDEOS_KEY);
      if (saved) {
        const records: SeenVideoRecord[] = JSON.parse(saved);
        this.seenVideos.clear();
        records.forEach(record => {
          this.seenVideos.set(record.id, record.seenAt);
        });
        console.log(`Loaded ${records.length} seen media from localStorage`);
      }
    } catch (error) {
      console.error('Error loading seen media:', error);
    }
    this.updateSeenCount();
  }

  /**
   * Save seen media to localStorage
   */
  private saveSeenVideos(): void {
    try {
      const records: SeenVideoRecord[] = Array.from(this.seenVideos.entries()).map(([id, seenAt]) => ({
        id,
        seenAt
      }));
      localStorage.setItem(this.SEEN_VIDEOS_KEY, JSON.stringify(records));
      console.log(`Saved ${records.length} seen media to localStorage`);
    } catch (error) {
      console.error('Error saving seen media:', error);
    }
  }

  /**
   * Mark a video as seen
   */
  markVideoAsSeen(videoId: number): void {
    const settings = this.getSeenSettings();
    if (!settings.enableSeenTracking) {
      return;
    }

    if (!this.seenVideos.has(videoId)) {
      this.seenVideos.set(videoId, Date.now());
      this.saveSeenVideos();
      this.updateSeenCount();
      console.log(`Marked video ${videoId} as seen`);
    }
  }

  /**
   * Check if a video has been seen
   */
  isVideoSeen(videoId: number): boolean {
    return this.seenVideos.has(videoId);
  }

  /**
   * Get list of seen video IDs
   */
  getSeenVideoIds(): number[] {
    return Array.from(this.seenVideos.keys());
  }

  /**
   * Get seen media count observable
   */
  getSeenVideosCount(): Observable<number> {
    return this.seenVideosCount$.asObservable();
  }

  /**
   * Get seen media count synchronously
   */
  getSeenVideosCountSync(): number {
    return this.seenVideos.size;
  }

  /**
   * Clear all seen media
   */
  clearSeenVideos(): void {
    this.seenVideos.clear();
    try {
      localStorage.removeItem(this.SEEN_VIDEOS_KEY);
      console.log('Cleared all seen media');
    } catch (error) {
      console.error('Error clearing seen media:', error);
    }
    this.updateSeenCount();
  }

  /**
   * Update the seen count subject
   */
  private updateSeenCount(): void {
    this.seenVideosCount$.next(this.seenVideos.size);
  }

  /**
   * Load seen persistence settings from localStorage
   */
  getSeenSettings(): SeenPersistenceSettings {
    try {
      const saved = localStorage.getItem(this.SEEN_SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        return {
          enableSeenTracking: settings.enableSeenTracking !== false, // default to true
          enableSeenBlacklist: settings.enableSeenBlacklist === true, // default to false
        };
      }
    } catch (error) {
      console.warn('Failed to load seen settings:', error);
    }

    return {
      enableSeenTracking: true,  // Default to enabled
      enableSeenBlacklist: false, // Default to disabled
    };
  }

  /**
   * Save seen persistence settings to localStorage
   */
  saveSeenSettings(settings: SeenPersistenceSettings): void {
    try {
      localStorage.setItem(this.SEEN_SETTINGS_KEY, JSON.stringify(settings));
      console.log('Seen settings saved:', settings);
    } catch (error) {
      console.warn('Failed to save seen settings:', error);
    }
  }
}